import {
	HTMLAttributes,
	PropsWithChildren,
	useEffect,
	useRef,
	useState,
} from 'react';
import { StyledFadeContainer } from './FadeContainer.styled';

type FadeContainerProps = PropsWithChildren &
	HTMLAttributes<HTMLDivElement> & {
		height: number;
		fadeSize?: number;
	};

const FadeContainer = ({
	children,
	height,
	fadeSize = 50,
	className,
	style,
	...rest
}: FadeContainerProps) => {
	const listRef = useRef<HTMLDivElement>(null);
	const [showTopMask, setShowTopMask] = useState(false);
	const [showBottomMask, setShowBottomMask] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		const updateMaskVisibility = () => {
			if (!listRef.current) {
				return;
			}

			const { scrollTop, scrollHeight, clientHeight } = listRef.current;
			const maxScrollTop = scrollHeight - clientHeight;

			setShowTopMask(scrollTop > 0);
			setShowBottomMask(scrollTop < maxScrollTop);
		};

		const listElement = listRef.current;

		if (!listElement) {
			return;
		}

		listElement.addEventListener('scroll', updateMaskVisibility);
		updateMaskVisibility();

		return () => {
			listElement.removeEventListener('scroll', updateMaskVisibility);
		};
	}, []);

	useEffect(() => {
		if (isInitialized) {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			setIsInitialized(true);
		}, 0);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [isInitialized]);

	return (
		<StyledFadeContainer
			ref={listRef}
			className={className}
			$animated={isInitialized}
			style={{
				...style,
				height,
				['--topFade' as any]: showTopMask ? `${fadeSize}px` : '0px',
				['--bottomFade' as any]: showBottomMask ? `${fadeSize}px` : '0px',
			}}
			{...rest}
		>
			{children}
		</StyledFadeContainer>
	);
};

export { FadeContainer };
